import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { HavePaginationEntity } from 'src/entities/HavePagination.entity';
import { PartnerCompanyEntity, PartnerCompanyMastersEntity, PartnerCompanyRequestEntity } from 'src/entities/PartnerCompanyEntity';
import requestUtil from 'src/utils/request.util';

const requestPathBase = '/api/v4/master/partner_companies';
export const queryKey = 'usePartnerCompaniesRequest';

const getResources = async (page: number, sortCondition: string, searchCondition: string, per?: number) => {
  const requestParams = [
    `page=${page + 1}`,
    sortCondition ? `sort_condition=${sortCondition}` : '',
    searchCondition ? `${searchCondition}` : '',
    per ? `per=${per}` : '',
  ].filter((maybe) => maybe).join('&');

  const requestPath = [
    requestPathBase,
    requestParams,
  ].join('?');

  const response: AxiosResponse<HavePaginationEntity<PartnerCompanyEntity>> = await axios.get<HavePaginationEntity<PartnerCompanyEntity>>(requestPath);

  return response.data;
};

export const usePartnerCompaniesRequest = (page: number, sortCondition: string, searchCondition: string, enabled: boolean, per?: number,) => useQuery(
  [queryKey, { page, sortCondition, searchCondition, per }],
  () => getResources(page, sortCondition, searchCondition, per),
  {
    staleTime: Infinity,
    enabled,
  }
);

const getMasters = async () => {
  const response: AxiosResponse<PartnerCompanyMastersEntity> = await axios.get<PartnerCompanyMastersEntity>('/api/v2/partner_companies/masters');
  return response.data;
};

export const usePartnerCompanyMastersRequest = () => useQuery(
  [queryKey, 'master'],
  () => getMasters(),
  {
    staleTime: Infinity,
  }
);

export const useMutationPartnerCompanies = () => {
  const queryClient = useQueryClient();
  return {
    addPartnerCompany: useMutation(
      (entity: PartnerCompanyRequestEntity) => axios.post('/api/v2/partner_companies', requestUtil.convertKeysToSnakeCase(entity)),
      {
        onSuccess: () => queryClient.invalidateQueries(['usePartnerCompaniesRequest'])
      }
    ),
    updatePartnerCompany: useMutation(
      (entity: PartnerCompanyEntity) => axios.put(`/api/v2/partner_companies/${entity.id}`, requestUtil.convertKeysToSnakeCase(entity)),
      {
        onSuccess: () => queryClient.invalidateQueries(['usePartnerCompaniesRequest'])
      }
    ),
    deletePartnerCompanies: useMutation(
      (ids: number[]) => axios.delete('/api/v2/partner_companies/bulk', { data: { ids } }),
      {
        onSuccess: () => {
          void queryClient.invalidateQueries(['usePartnerCompaniesRequest']);
        }
      }
    )
  };
};
